<template>
  <v-container>
    <v-card class="mx-auto" min-width="384px" max-width="640px">
      <v-card-title class="blue-grey white--text">
        <h3>Set-top-box configuration</h3>
      </v-card-title>
      <v-form ref="form" v-model="isFormValid" lazy-validation>
        <v-tabs v-model="activeTab">
          <v-tab>Summary</v-tab>
          <v-tab :disabled="!id && !prefillProductVersion">Properties</v-tab>
          <v-tab :disabled="!id || !isOwner">Delegated roles</v-tab>
          <v-tab-item>
            <v-card-text>
              <h3 v-if="!!id">
                <v-icon class="pr-2">mdi-ethernet</v-icon>
                MAC address: {{macAddress}}
              </h3>
              <v-text-field
                v-if="!!id"
                label="Label / Description"
                v-model="label"
                prepend-icon="mdi-label"
              />
              <v-select :items="usageTypeItems" v-model="usageType" label="Usage type" dense></v-select>
              <v-autocomplete
                label="Owner"
                clearable
                prepend-icon="mdi-account-circle"
                return-object
                v-model="owner.selected"
                chips
                :items="owner.items"
                :loading="owner.loading"
                :readonly="!isOwner && !!id"
                :search-input.sync="owner.search"
                :rules="[v => !id || !!v || 'The owner is required']"
                item-text="text"
                item-value="value"
                dense
              ></v-autocomplete>
              <v-radio-group
                v-if="!id"
                v-model="tag.operationType"
                label="Operation on tags:"
                row
                class="pa-0 ma-0"
              >
                <v-radio label="Add to list" value="add"></v-radio>
                <v-radio label="Set the list" value="set"></v-radio>
              </v-radio-group>
              <v-combobox
                v-model="tag.values"
                :items="configTagUserList"
                chips
                clearable
                label="Tags list"
                multiple
                prepend-icon="mdi-tag-multiple"
                solo
              >
                <template v-slot:selection="{ attrs, item, select, selected }">
                  <v-chip
                    v-bind="attrs"
                    :input-value="selected"
                    close
                    @click="select"
                    @click:close="tagRemove(item)"
                  >
                    <strong>{{ item }}</strong>
                  </v-chip>
                </template>
              </v-combobox>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <v-radio-group
                v-model="productVersion"
                label="Product:"
                row
                class="pa-0"
                :readonly="prefillProductVersion !== null"
              >
                <v-radio
                  v-for="(item, index) in productVersionList"
                  v-bind:key="index"
                  :label="item"
                  :value="item"
                ></v-radio>
              </v-radio-group>
              <v-select
                :items="operatorItems"
                v-model="operator"
                label="Operator"
                item-text="text"
                item-value="value"
                dense
              ></v-select>
              <v-autocomplete
                label="Main image / Application"
                clearable
                prepend-icon="mdi-application"
                v-model="appli.selected"
                return-object
                chips
                :items="appli.items"
                :loading="appli.loading"
                :search-input.sync="appli.search"
                item-text="text"
                item-value="value"
                dense
              ></v-autocomplete>
              <v-autocomplete
                label="User interface"
                clearable
                prepend-icon="mdi-television-guide"
                v-model="ui.selected"
                return-object
                chips
                :items="ui.items"
                :loading="ui.loading"
                :search-input.sync="ui.search"
                item-text="text"
                item-value="value"
                dense
              ></v-autocomplete>
              <v-autocomplete
                label="Recovery image / Bootstrap loader"
                clearable
                prepend-icon="mdi-restore-alert"
                v-model="bsl.selected"
                return-object
                chips
                :items="bsl.items"
                :loading="bsl.loading"
                :search-input.sync="bsl.search"
                item-text="text"
                item-value="value"
                dense
              ></v-autocomplete>
              <v-autocomplete
                label="Data image"
                clearable
                prepend-icon="mdi-database"
                v-model="data.selected"
                return-object
                chips
                :items="data.items"
                :loading="data.loading"
                :search-input.sync="data.search"
                item-text="text"
                item-value="value"
                dense
              ></v-autocomplete>
            </v-card-text>
            <v-divider></v-divider>
          </v-tab-item>
          <v-tab-item>
            <v-card class="pa-2" v-if="isOwner">
              <v-card-subtitle>
                <h4>
                  <v-icon class="pr-6">mdi-account-group</v-icon>Role list
                </h4>
              </v-card-subtitle>
              <v-list class="delegates">
                <v-list-item v-for="(delegate, index) in delegateList" :key="index" class="ma-1">
                  <v-list-item-content class="ma-0 pa-0">
                    <v-list-item-title>{{delegate.text}}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="ma-0 pa-0">
                    <v-btn color="red" @click="delegateRemove(index)">
                      <v-icon>mdi-account-remove</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-autocomplete
                label="Add a delegated role"
                clearable
                chips
                v-model="delegate.selected"
                return-object
                :items="delegate.items"
                :loading="delegate.loading"
                :search-input.sync="delegate.search"
                item-text="text"
                item-value="value"
                class="px-4"
                @keypress.enter="delegateAdd"
                dense
              >
                <template v-slot:append>
                  <v-btn depressed class="ma-0" @click="delegateAdd" color="info">
                    <v-icon left>mdi-account-plus</v-icon>
                  </v-btn>
                </template>
              </v-autocomplete>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-form>
      <v-card-actions>
        <v-btn
          v-if="!!id && activeTab != 2"
          @click="validate"
          color="info"
          :disabled="!isFormValid"
        >
          <v-icon>mdi-content-save</v-icon>Save
        </v-btn>
        <v-btn v-if="!id" @click="applyAll" color="info" :disabled="applyAllDisabled">
          <v-icon>mdi-download-multiple</v-icon>Apply to selection
        </v-btn>
        <v-btn v-if="activeTab != 2" @click="close()" color="orange white--text">
          <v-icon>mdi-cancel</v-icon>Cancel
        </v-btn>
        <v-btn v-if="activeTab === 2" @click="close()" color="info">
          <v-icon>mdi-close-circle</v-icon>Close
        </v-btn>
        <v-btn v-if="activeTab !== 2" @click="reset" color="deep-purple white--text">
          <v-icon>mdi-restore</v-icon>Reset
        </v-btn>
        <v-btn v-if="!!id && isOwner" @click="remove" color="red white--text">
          <v-icon>mdi-delete</v-icon>Delete
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="dialogDelete" max-width="380">
      <v-card>
        <v-card-title class="headline">Configuration delete</v-card-title>
        <v-card-text>
          <p>Do-you really want to remove the set-top-box {{stbId}} configuration?</p>
          <p>This operation is irreversible!</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="brown white--text" @click="dialogDeleteConfirm()">
            <v-icon>mdi-delete-forever</v-icon>Confirm
          </v-btn>
          <v-btn color="orange white--text" @click="dialogDeleteCancel()">
            <v-icon>mdi-cancel</v-icon>Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.v-list-item {
  min-height: 0px;
}
.v-list.delegates {
  height: 256px;
  overflow-y: auto;
}
.v-dialog.v-dialog--active .v-card__title {
  cursor: grab;
}
.v-dialog.v-dialog--active .v-card__title:active {
  cursor: grabbing;
}
</style>

<script>
import {
  getOperatorList,
  getTextFromUserRecord,
  getTextFromRoleRecord,
  getOperatorTitle,
  vAutoCompDefData,
  autocompleteListUpdate,
  getStbConfigUsageTypeList,
  getStbConfigProductVersionList
} from "./../lib";

export default {
  name: "STBConfig",
  props: {
    id: {
      type: String,
      default: null
    },
    tab: {
      type: String,
      default: "summary",
      validator: value =>
        ["summary", "properties", "delegations"].indexOf(value) !== -1
    },
    prefillProductVersion: {
      type: String,
      default: null
    }
  },
  components: {},
  data: () => ({
    isFormValid: false,
    tag: {
      items: [],
      values: [],
      operationType: "add"
    },
    usageTypeItems: [],
    productVersionList: getStbConfigProductVersionList(),
    appli: Object.assign(vAutoCompDefData(), {
      rest: {
        path: c =>
          c.productVersion
            ? `/listings/appli.${c.productVersion.toLowerCase() +
                (c.operator ? "." + c.operator : "")}`
            : null,
        args: {
          sort_asc: false,
          limit: 65535
        },
        mapCallback: e => ({
          value: e.value,
          text: e.metadata.title
        })
      }
    }),
    ui: Object.assign(vAutoCompDefData(), {
      rest: {
        path: c =>
          c.productVersion
            ? `/listings/ui.${c.productVersion.toLowerCase() +
                (c.operator ? "." + c.operator : "")}`
            : null,
        args: {
          sort_asc: false,
          limit: 65535
        },
        mapCallback: e => ({
          value: e.value,
          text: e.metadata.title
        })
      }
    }),
    bsl: Object.assign(vAutoCompDefData(), {
      rest: {
        path: c =>
          c.productVersion
            ? `/listings/bsl.${c.productVersion.toLowerCase()}`
            : null,
        args: {
          sort_asc: false,
          limit: 65535
        },
        mapCallback: e => ({
          value: e.value,
          text: e.metadata.title
        })
      }
    }),
    data: Object.assign(vAutoCompDefData(), {
      rest: {
        path: c =>
          c.productVersion
            ? `/listings/data.${c.productVersion.toLowerCase() +
                (c.operator ? "." + c.operator : "")}`
            : null,
        args: {
          sort_asc: false,
          limit: 65535
        },
        mapCallback: e => ({
          value: e.value,
          text: e.metadata.title
        })
      }
    }),
    delegateList: [],
    isOwner: false,
    owner: Object.assign(vAutoCompDefData(), {
      rest: {
        path: () => "/users",
        args: {
          sort_asc: true,
          limit: 65535
        },
        mapCallback: e => ({
          value: e.user_id,
          text: getTextFromUserRecord(e)
        })
      }
    }),
    delegate: Object.assign(vAutoCompDefData(), {
      rest: {
        path: () => "/roles",
        args: {
          sort_asc: true,
          limit: 65535,
          extended_format: true
        },
        mapCallback: e => ({
          value: e.role_id,
          text: getTextFromRoleRecord(e)
        })
      }
    }),
    stbId: null,
    label: "",
    productVersion: null,
    usageType: "",
    operator: null,
    operatorItems: getOperatorList(),
    dialogDelete: false,
    activeTab: 0
  }),
  watch: {
    id: function(value) {
      this.load(value);
    },
    "owner.search": function(value) {
      this.updateList("owner", value);
    },
    prefillProductVersion: function(version) {
      if (version) this.productVersion = version;
    },
    productVersion: function(_, old) {
      if (old !== null)
        ["appli", "ui", "bsl", "data"].forEach(p => {
          this[p].selected = null;
          this[p].search = "";
        });
    },
    operator: function(_, old) {
      if (old !== null)
        ["appli", "ui", "bsl", "data"].forEach(p => {
          this[p].selected = null;
          this[p].search = "";
        });
    },
    "delegate.search": function(value) {
      this.updateList("delegate", value);
    },
    "appli.search": function(value) {
      this.updateList("appli", value);
    },
    "ui.search": function(value) {
      this.updateList("ui", value);
    },
    "bsl.search": function(value) {
      this.updateList("bsl", value);
    },
    "data.search": function(value) {
      this.updateList("data", value);
    },
    tab: function(value) {
      this.setTab(value);
    }
  },
  computed: {
    macAddress() {
      return this.stbId ? this.stbId.match(/.{2}/g).join(":") : "";
    },
    configTagUserList() {
      return this.$store.getters.configTagUserList;
    },
    applyAllDisabled() {
      return !(
        this.appli.selected ||
        this.ui.selected ||
        this.bsl.selected ||
        this.data.selected ||
        this.owner.selected ||
        this.usageType !== "Unchanged" ||
        this.tag.values.length !== 0 ||
        this.tag.operationType === "set"
      );
    }
  },
  created() {
    this.delegate.rest.filterCallback = e =>
      !this.delegateList.some(m => m.value === e.role_id);
    // Filtre UI pour tag WTV
    this.ui.rest.filterCallback = e => {
      const hasWTV = this.tag?.values?.includes("WTV");
      if (hasWTV) {
        const title = e?.metadata?.title || "";
        // keep onny v1.x.x
        return /^v1\.\d+\.\d+/.test(title);
      }
      return true;
    };
    this.setTab(this.tab);
    this.load(this.id);
  },
  methods: {
    tagRemove(item) {
      this.tag.values.splice(this.tag.values.indexOf(item), 1);
      this.tag.values = [...this.tag.values];
    },
    setTab(value) {
      if (!value) return;
      switch (value) {
        case "summary":
          this.activeTab = 0;
          break;
        case "properties":
          this.activeTab = 1;
          break;
        case "delegations":
          this.activeTab = 2;
          break;
      }
    },
    reset() {
      this.load(this.stbId);
    },
    load(id) {
      ["appli", "ui", "bsl", "data"].forEach(p => {
        this[p].selected = null;
        this[p].search = null;
      });
      ["productVersion", "operator"].forEach(p => {
        this[p] = null;
      });
      this.delegateList = [];
      this.stbId = id;
      if (this.stbId) {
        this.usageTypeItems = getStbConfigUsageTypeList().concat([]);
        this.$axios
          .get("/configs/" + this.stbId)
          .then(result => this.fillFormData(result.data))
          .catch(result => {
            this.processErrorResponse(result);
            this.close();
          });
      } else {
        this.usageTypeItems = getStbConfigUsageTypeList().concat(["Unchanged"]);
        this.tag.operationType = "add";
        this.fillFormData({
          properties: {
            x_usage_type: "Unchanged",
            x_product_version: this.prefillProductVersion
          }
        });
      }
    },
    fillFormData(data) {
      this.label = data.label;
      this.owner.selected = data.owner_id ? { value: data.owner_id } : null;
      this.owner.search = data.owner_id;
      const props = data.properties || {};
      this.tag.values = props.x_tags || [];
      if (props.x_product_version) {
        this.productVersion = props.x_product_version;
      } else {
        this.productVersion = "STB8";
      }
      if (props.x_usage_type) {
        this.usageType = props.x_usage_type;
      } else {
        this.usageType = "Development";
      }
      this.operator = props.x_operator || "";
      ["appli", "ui", "bsl", "data"].forEach(p => {
        this[p].selected = props[p] ? { value: props[p] } : null;
        this[p].search = props[p] ? props[p] : "";
      });
      this.isOwner =
        this.$store.getters.userIsAdmin ||
        this.owner.selected.value === this.$store.getters.userId;
      this.$axios
        .get("/configs/" + this.stbId + "/delegations")
        .then(result => {
          this.delegateList = result.data.map(elt => ({
            value: elt.role_id,
            text: getTextFromRoleRecord(elt)
          }));
        })
        .catch(result => {
          this.processErrorResponse(result);
          this.close();
        });
    },
    delegateRemove(index) {
      const value = this.delegateList[index].value;
      this.$axios
        .delete("/configs/" + this.stbId + "/delegations/" + value)
        .then(() => {
          const text = this.delegateList[index].text;
          this.delegateList.splice(index, 1);
          this.$emit(
            "success",
            `${text} successfully removed from the delegation list`
          );
        })
        .catch(result => {
          this.processErrorResponse(result);
        });
    },
    delegateAdd() {
      const delegate = this.delegate.selected;
      if (!delegate) {
        return;
      }
      this.$axios
        .post("/configs/" + this.stbId + "/delegations", {
          role_id: delegate.value
        })
        .then(() => {
          this.delegateList.push({
            value: delegate.value,
            text: delegate.text
          });
          this.delegate.selected = null;
          this.delegate.search = "";
          this.$emit(
            "success",
            `${delegate.text} successfully added to the delegation list`
          );
        })
        .catch(result => {
          this.processErrorResponse(result);
        });
    },
    processErrorResponse(result) {
      this.$emit("error", result);
    },
    validate() {
      if (this.$refs.form.validate()) {
        const productVersion = this.productVersion;
        const operator = this.operator;
        var label;
        if (this.label != "") {
          label = this.label;
        } else {
          label = null;
        }
        var displayTitles = {};
        displayTitles[this.owner.selected.value] = this.owner.selected.text;
        var props = {};
        ["appli", "ui", "bsl", "data"].forEach(p => {
          if (this[p].selected) {
            props[p] = this[p].selected.value;
            displayTitles[props[p]] = this[p].selected.text;
          } else {
            props[p] = null;
            delete displayTitles[props[p]];
          }
        });
        this.configTagAdd(this.tag.values);
        this.$axios
          .put("/configs/" + this.stbId, {
            label: label,
            owner_id: this.owner.selected.value,
            set_properties: {
              appli: props.appli,
              ui: props.ui,
              bsl: props.bsl,
              data: props.data,
              x_tags: this.tag.values,
              x_product_version: productVersion,
              x_usage_type: this.usageType,
              x_operator: operator,
              x_display_titles: displayTitles
            }
          })
          .then(() => {
            this.$emit("success", "Configuration successfully updated");
            this.close({
              type: "update",
              data: {
                id: this.stbId,
                label: label || productVersion,
                mac: this.stbId.match(/.{2}/g).join(":"),
                owner: this.owner.selected.text,
                ownerId: this.owner.selected.value,
                productVersion: productVersion,
                usageType: this.usageType,
                operator: getOperatorTitle(this.operator),
                appli: this.appli.selected ? this.appli.selected.text : "",
                ui: this.ui.selected ? this.ui.selected.text : "",
                bsl: this.bsl.selected ? this.bsl.selected.text : "",
                data: this.data.selected ? this.data.selected.text : "",
                tags: this.tag.values.concat([this.usageType, productVersion])
              }
            });
          })
          .catch(result => {
            this.processErrorResponse(result);
          });
      }
    },
    applyAll() {
      this.close({
        type: "updateSelection",
        data: {
          properties: ["appli", "ui", "bsl", "data"]
            .filter(p => this[p].selected)
            .map(p => ({ name: p, data: this[p].selected })),
          owner: this.owner.selected,
          tags: this.tag.values,
          tagOperationType: this.tag.operationType,
          usageType: this.usageType !== "Unchanged" ? this.usageType : null
        }
      });
      this.load(null);
    },
    remove() {
      this.dialogDeleteAction = () => {
        this.$axios
          .delete("/configs/" + this.stbId)
          .then(() => {
            this.$emit("success", "Configuration successfully removed");
            this.close({ type: "delete" });
          })
          .catch(result => {
            this.processErrorResponse(result);
          });
      };
      this.dialogDelete = true;
    },
    dialogDeleteConfirm() {
      this.dialogDeleteAction();
      this.dialogDeleteAction = null;
      this.dialogDelete = false;
    },
    dialogDeleteCancel() {
      this.dialogDeleteAction = null;
      this.dialogDelete = false;
    },
    close(result) {
      this.$emit("close", result);
    },
    updateList(name, filter) {
      autocompleteListUpdate(this, name, filter);
    },
    configTagAdd(value) {
      this.$store.commit("config_tag_add", value);
    }
  }
};
</script>
